import { useCallback } from 'react';

import { useCreateStoreMutation } from '@/api/StoreQuery';
import type { IStoreCartDesignGroupShell } from '@/modules/Cart/StoreCart/helpers/storeCartDesignGroups.utils';
import { buildCreateStorePayloadFromCartItems } from '@/modules/Cart/StoreCart/helpers/storeCartCreateStorePayload.utils';
import { parseCreateStoreMutationStoreId } from '@/modules/Cart/StoreCart/helpers/storeCartCreateStoreResponse.utils';
import {
  buildStoreCheckoutUrl,
  persistStoreCartDesignGroupMinimums,
} from '@/modules/Cart/StoreCart/helpers/storeCartNavigation.utils';
import { STORE_CART_CREATE_STORE_ERROR_MSG } from '@/modules/Cart/StoreCart/storeCart.constants';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import { captureStoresFormViewed } from '@/mixpanel/MixpanelEvents';
import { useAppSelector } from '@/store/hooks';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import type { RootState } from '@/store';

export const useSetupEventStore = () => {
  const user = useAppSelector((state: RootState) => state.user.user);
  const userId = user?.id;
  const [createStore, { isLoading: isSettingUp }] = useCreateStoreMutation();

  const setupEventStore = useCallback(
    async (
      cartItems: ICartItemDetail[],
      designGroupShells: Record<string, IStoreCartDesignGroupShell> = {}
    ) => {
      const payload = buildCreateStorePayloadFromCartItems(
        cartItems,
        designGroupShells,
        userId,
        user?.full_name,
        user?.manager_id,
        user?.manager_name
      );
      const result = await createStore(payload);
      const storeId = parseCreateStoreMutationStoreId(result);

      if (storeId == null) {
        showToastrMessage(STORE_CART_CREATE_STORE_ERROR_MSG, 'failure');
        return;
      }

      captureStoresFormViewed(user?.email ?? '');

      persistStoreCartDesignGroupMinimums(cartItems);
      // Keep the cart overlay open until assign unloads the page — closing it
      // first flashes HQ, and closing + soft-navigate races URL sync (cart
      // closes but checkout never loads).
      globalThis.location.assign(buildStoreCheckoutUrl(storeId));
    },
    [createStore, user?.email, user?.full_name, user?.manager_id, user?.manager_name, userId]
  );

  return {
    setupEventStore,
    isSettingUp,
  };
};
